import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { 
  User, 
  Calendar, 
  FileText, 
  Settings, 
  Shield, 
  Mail, 
  Clock,
  CheckCircle,
  AlertCircle,
  ArrowRight,
  Bell,
  Download
} from 'lucide-react';
import { RootState } from '../store';
import { clientAuthApi } from '../services/clientAuthApi';
import { ClientUser } from '@digitaltattoo/types';
import AccountLayout from '../components/AccountLayout';

interface DashboardStats {
  totalAppointments: number;
  upcomingAppointments: number;
  completedAppointments: number;
  documentsCount: number;
  lastLoginAt: string;
}

export function AccountDashboard() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useSelector((state: RootState) => state.auth);
  
  const [stats, setStats] = useState<DashboardStats>({
    totalAppointments: 0,
    upcomingAppointments: 0,
    completedAppointments: 0,
    documentsCount: 0,
    lastLoginAt: ''
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated || !user) {
      navigate('/login');
      return;
    }
    
    loadDashboardData();
  }, [isAuthenticated, user, navigate]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      // TODO: Replace with actual API calls when backend endpoints are ready
      // For now, we'll simulate the data
      setStats({
        totalAppointments: 3,
        upcomingAppointments: 1,
        completedAppointments: 2,
        documentsCount: 5,
        lastLoginAt: new Date().toISOString()
      });
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const quickActions = [
    {
      icon: Calendar,
      title: t('auth.account.dashboard.quickActions.bookAppointment'),
      description: t('auth.account.dashboard.quickActions.bookAppointmentDesc'),
      href: '/book-piercing?fresh=1',
      color: 'from-blue-500 to-blue-600',
      bgColor: 'bg-blue-50',
      iconColor: 'text-blue-600'
    },
    {
      icon: FileText,
      title: t('auth.account.dashboard.quickActions.viewDocuments'),
      description: t('auth.account.dashboard.quickActions.viewDocumentsDesc'),
      href: '/account/documents',
      color: 'from-green-500 to-green-600',
      bgColor: 'bg-green-50',
      iconColor: 'text-green-600'
    },
    {
      icon: Settings,
      title: t('auth.account.dashboard.quickActions.updateProfile'),
      description: t('auth.account.dashboard.quickActions.updateProfileDesc'),
      href: '/account/profile',
      color: 'from-purple-500 to-purple-600',
      bgColor: 'bg-purple-50',
      iconColor: 'text-purple-600'
    }
  ];

  const accountStatus = [
    {
      icon: user?.emailVerified ? CheckCircle : AlertCircle,
      title: t('auth.account.dashboard.status.emailVerification'),
      status: user?.emailVerified ? 'verified' : 'unverified',
      description: user?.emailVerified 
        ? t('auth.account.dashboard.status.emailVerified')
        : t('auth.account.dashboard.status.emailUnverified'),
      action: user?.emailVerified ? null : {
        label: t('auth.account.dashboard.status.verifyEmail'),
        href: '/account/verify-email'
      },
      color: user?.emailVerified ? 'text-green-600' : 'text-yellow-600',
      bgColor: user?.emailVerified ? 'bg-green-50' : 'bg-yellow-50'
    },
    {
      icon: Shield,
      title: t('auth.account.dashboard.status.accountSecurity'),
      status: 'secure',
      description: t('auth.account.dashboard.status.accountSecure'),
      action: {
        label: t('auth.account.dashboard.status.changePassword'),
        href: '/account/security'
      },
      color: 'text-green-600',
      bgColor: 'bg-green-50'
    }
  ];

  if (loading) {
    return (
      <AccountLayout>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
            <p className="text-textSecondary">{t('auth.account.dashboard.loading')}</p>
          </div>
        </div>
      </AccountLayout>
    );
  }

  return (
    <AccountLayout>
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-display font-bold text-textPrimary">
                {t('auth.account.dashboard.welcome', { name: user?.firstName })}
              </h1>
              <p className="text-textSecondary mt-1">
                {t('auth.account.dashboard.subtitle')}
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <div className="text-right">
                <p className="text-sm text-textSecondary">
                  {t('auth.account.dashboard.lastLogin')}
                </p>
                <p className="text-sm font-medium text-textPrimary">
                  {new Date(stats.lastLoginAt).toLocaleDateString()}
                </p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-primary-300 to-accent-500 rounded-full flex items-center justify-center">
                <span className="text-white font-medium text-lg">
                  {user?.firstName?.charAt(0).toUpperCase()}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Calendar className="h-8 w-8 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-textSecondary">
                  {t('auth.account.dashboard.stats.totalAppointments')}
                </p>
                <p className="text-2xl font-bold text-textPrimary">
                  {stats.totalAppointments}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Clock className="h-8 w-8 text-orange-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-textSecondary">
                  {t('auth.account.dashboard.stats.upcomingAppointments')}
                </p>
                <p className="text-2xl font-bold text-textPrimary">
                  {stats.upcomingAppointments}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-textSecondary">
                  {t('auth.account.dashboard.stats.completedAppointments')}
                </p>
                <p className="text-2xl font-bold text-textPrimary">
                  {stats.completedAppointments}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <FileText className="h-8 w-8 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-textSecondary">
                  {t('auth.account.dashboard.stats.documents')}
                </p>
                <p className="text-2xl font-bold text-textPrimary">
                  {stats.documentsCount}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Quick Actions */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-sm border">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-textPrimary">
                  {t('auth.account.dashboard.quickActions.title')}
                </h2>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {quickActions.map((action, index) => (
                    <button
                      key={index}
                      onClick={() => navigate(action.href)}
                      className="group p-4 rounded-lg border border-gray-200 hover:border-gray-300 hover:shadow-md transition-all duration-200 text-left"
                    >
                      <div className={`w-12 h-12 ${action.bgColor} rounded-lg flex items-center justify-center mb-3 group-hover:scale-105 transition-transform`}>
                        <action.icon className={`h-6 w-6 ${action.iconColor}`} />
                      </div>
                      <h3 className="font-medium text-textPrimary mb-1">
                        {action.title}
                      </h3>
                      <p className="text-sm text-textSecondary mb-3">
                        {action.description}
                      </p>
                      <div className="flex items-center text-sm fo-link group-hover:no-underline">
                        <span>{t('auth.account.dashboard.quickActions.getStarted')}</span>
                        <ArrowRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Account Status */}
          <div>
            <div className="bg-white rounded-lg shadow-sm border">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-textPrimary">
                  {t('auth.account.dashboard.status.title')}
                </h2>
              </div>
              <div className="p-6 space-y-4">
                {accountStatus.map((status, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className={`flex-shrink-0 w-10 h-10 ${status.bgColor} rounded-lg flex items-center justify-center`}>
                      <status.icon className={`h-5 w-5 ${status.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-medium text-textPrimary">
                        {status.title}
                      </h3>
                      <p className="text-sm text-textSecondary mt-1">
                        {status.description}
                      </p>
                      {status.action && (
                        <button
                          onClick={() => navigate(status.action!.href)}
                          className="text-sm fo-link font-medium mt-2"
                        >
                          {status.action.label}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white rounded-lg shadow-sm border mt-6">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-textPrimary">
                  {t('auth.account.dashboard.recentActivity.title')}
                </h2>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-50 rounded-full flex items-center justify-center">
                      <Calendar className="h-4 w-4 text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-textPrimary">
                        {t('auth.account.dashboard.recentActivity.lastLogin')}
                      </p>
                      <p className="text-xs text-textSecondary">
                        {new Date(stats.lastLoginAt).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-green-50 rounded-full flex items-center justify-center">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-textPrimary">
                        {t('auth.account.dashboard.recentActivity.accountCreated')}
                      </p>
                      <p className="text-xs text-textSecondary">
                        {user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AccountLayout>
  );
}

export default AccountDashboard;
